// src/pages/FinalizarCadastro.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth, storage } from '../services/firebase';
import { signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

export default function FinalizarCadastro() {
  const authContext = useAuth();
  const usuario = authContext?.usuario;
  const loadingUserDoc = authContext?.loadingUserDoc;

  const [dados, setDados] = useState({
    nome: '',
    email: '',
    telefone: '',
    creci: '',
    cpf: '',
    cidade: '',
    bairro: '',
    rua: '',
    numero: '',
    senha: '',
    confirmarSenha: '',
    tipoConta: '',
  });

  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [senhaAtual, setSenhaAtual] = useState('');
  const [mostrarReautenticacao, setMostrarReautenticacao] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDados = async () => {
      if (!usuario?.email || !usuario?.uid) return;

      setLoading(true);
      setErro('');

      try {
        const cadastroRapidoDocRef = doc(
          db,
          'cadastros_rapidos',
          usuario.email
        );

        const cadastroRapidoSnap = await getDoc(cadastroRapidoDocRef);

        if (cadastroRapidoSnap.exists()) {
          const dadosIniciais = cadastroRapidoSnap.data();

          setDados((prev) => ({
            ...prev,
            email: usuario.email,
            nome: dadosIniciais.nome || '',
            telefone: dadosIniciais.whatsapp || '',
            tipoConta: dadosIniciais.tipo || '',
          }));
        } else {
          setDados((prev) => ({
            ...prev,
            email: usuario.email,
          }));
        }

        const userDocRef = doc(db, 'users', usuario.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();

          if (userData.cadastroCompleto) {
            navigate('/');
            return;
          }

          setDados((prev) => ({
            ...prev,
            nome: userData.nome || prev.nome,
            email: userData.email || prev.email,
            telefone: userData.telefone || prev.telefone,
            creci: userData.creci || prev.creci,
            cpf: userData.cpf || prev.cpf,
            cidade: userData.endereco?.cidade || prev.cidade,
            bairro: userData.endereco?.bairro || prev.bairro,
            rua: userData.endereco?.rua || prev.rua,
            numero: userData.endereco?.numero || prev.numero,
            tipoConta: userData.tipoConta || prev.tipoConta,
          }));

          if (userData.fotoPerfilUrl) {
            setFotoPreview(userData.fotoPerfilUrl);
          }
        }
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setErro('Erro ao carregar seus dados. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchDados();
  }, [usuario, navigate]);

  const handleChange = (e) => {
    setDados((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setFotoPerfil(file);
    setFotoPreview(URL.createObjectURL(file));
  };

  const salvarCadastro = async () => {
    let fotoPerfilUrl = fotoPreview || '';

    if (fotoPerfil && usuario?.uid) {
      const storageRef = ref(storage, `fotos_perfil/${usuario.uid}.jpg`);
      await uploadBytes(storageRef, fotoPerfil);
      fotoPerfilUrl = await getDownloadURL(storageRef);
    }

    const cadastroRapidoDocRef = doc(db, 'cadastros_rapidos', dados.email);
    const cadastroRapidoSnap = await getDoc(cadastroRapidoDocRef);

    if (cadastroRapidoSnap.exists()) {
      await deleteDoc(cadastroRapidoDocRef);
    }

    await setDoc(
      doc(db, 'users', usuario.uid),
      {
        uid: usuario.uid,
        email: dados.email,
        nome: dados.nome,
        telefone: dados.telefone,
        creci: dados.creci,
        cpf: dados.cpf,
        endereco: {
          cidade: dados.cidade,
          bairro: dados.bairro,
          rua: dados.rua,
          numero: dados.numero,
        },
        cadastroCompleto: true,
        status: 'ativo',
        tipoConta: dados.tipoConta || 'corretor',
        fotoPerfilUrl,
        atualizadoEm: serverTimestamp(),
        criadoEm: usuario.criadoEm || serverTimestamp(),
      },
      { merge: true }
    );

    navigate('/');
  };

  const validarFormulario = () => {
    if (
      !dados.nome ||
      !dados.email ||
      !dados.telefone ||
      !dados.creci ||
      !dados.cpf ||
      !dados.cidade ||
      !dados.bairro ||
      !dados.rua ||
      !dados.numero ||
      !dados.senha ||
      !dados.confirmarSenha
    ) {
      setErro('Preencha todos os campos obrigatórios.');
      return false;
    }

    if (dados.senha !== dados.confirmarSenha) {
      setErro('As senhas não coincidem.');
      return false;
    }

    if (
      dados.senha.length < 8 ||
      !/\d/.test(dados.senha) ||
      !/[A-Za-z]/.test(dados.senha)
    ) {
      setErro(
        'A senha deve ter pelo menos 8 caracteres, incluindo letras e números.'
      );
      return false;
    }

    return true;
  };

  const handleReautenticacao = async () => {
    setLoading(true);
    setErro('');

    try {
      if (!senhaAtual) {
        setErro('Por favor, insira sua senha atual.');
        setLoading(false);
        return;
      }

      if (!auth.currentUser?.email) {
        setErro('Não foi possível reautenticar. Faça login novamente.');
        setLoading(false);
        return;
      }

      await signInWithEmailAndPassword(
        auth,
        auth.currentUser.email,
        senhaAtual
      );

      await updatePassword(auth.currentUser, dados.senha);

      setMostrarReautenticacao(false);
      setSenhaAtual('');

      await salvarCadastro();
    } catch (reauthError) {
      console.error('Erro ao reautenticar:', reauthError);

      if (reauthError.code === 'auth/wrong-password') {
        setErro('Senha atual incorreta.');
      } else {
        setErro(`Erro ao reautenticar: ${reauthError.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setErro('');

    if (!usuario?.uid) {
      setErro('Usuário não autenticado. Faça login novamente.');
      setLoading(false);
      return;
    }

    if (!validarFormulario()) {
      setLoading(false);
      return;
    }

    try {
      if (auth.currentUser) {
        try {
          await updatePassword(auth.currentUser, dados.senha);
        } catch (error) {
          if (error.code === 'auth/requires-recent-login') {
            setErro('Confirme sua senha atual para continuar.');
            setMostrarReautenticacao(true);
            setLoading(false);
            return;
          }

          throw error;
        }
      }

      await salvarCadastro();
    } catch (error) {
      console.error('Erro ao finalizar cadastro:', error);

      if (error.code === 'auth/weak-password') {
        setErro('Senha muito fraca.');
      } else {
        setErro(`Erro: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingUserDoc || !usuario) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <p className="text-center">Carregando usuário...</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Finalizar Cadastro</h1>

      <p className="mb-4">Complete seus dados e defina sua senha definitiva.</p>

      {erro && (
        <div className="mb-4 text-red-600 border border-red-300 bg-red-50 p-2 rounded">
          {erro}
        </div>
      )}

      {mostrarReautenticacao && (
        <div className="mt-4 mb-4 p-4 border border-blue-300 bg-blue-50 rounded">
          <p className="mb-2">Confirme sua senha atual para continuar:</p>

          <input
            type="password"
            name="senhaAtual"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            className="w-full border p-2 rounded mb-2"
          />

          <button
            type="button"
            onClick={handleReautenticacao}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Confirmando...' : 'Confirmar Senha'}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block mb-1 font-medium">Foto de Perfil</label>

          <input type="file" accept="image/*" onChange={handleFotoChange} />

          {fotoPreview && (
            <img
              src={fotoPreview}
              alt="Preview"
              className="w-24 h-24 mt-2 rounded-full object-cover border"
            />
          )}
        </div>

        <input
          type="text"
          name="nome"
          placeholder="Nome Completo"
          value={dados.nome}
          onChange={handleChange}
          className="w-full border p-2 rounded"
        />

        <input
          type="email"
          name="email"
          placeholder="E-mail"
          value={dados.email}
          disabled
          className="w-full border p-2 rounded bg-gray-100"
        />

        <input
          type="text"
          name="telefone"
          placeholder="Telefone"
          value={dados.telefone}
          onChange={handleChange}
          className="w-full border p-2 rounded"
        />

        <input
          type="text"
          name="creci"
          placeholder="Número do CRECI"
          value={dados.creci}
          onChange={handleChange}
          className="w-full border p-2 rounded"
        />

        <input
          type="text"
          name="cpf"
          placeholder="CPF"
          value={dados.cpf}
          onChange={handleChange}
          className="w-full border p-2 rounded"
        />

        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            name="cidade"
            placeholder="Cidade"
            value={dados.cidade}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />

          <input
            type="text"
            name="bairro"
            placeholder="Bairro"
            value={dados.bairro}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            name="rua"
            placeholder="Rua"
            value={dados.rua}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />

          <input
            type="text"
            name="numero"
            placeholder="Nº"
            value={dados.numero}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />
        </div>

        <input
          type="password"
          name="senha"
          placeholder="Nova Senha"
          value={dados.senha}
          onChange={handleChange}
          className="w-full border p-2 rounded"
        />

        <input
          type="password"
          name="confirmarSenha"
          placeholder="Repita a Senha"
          value={dados.confirmarSenha}
          onChange={handleChange}
          className="w-full border p-2 rounded"
        />

        <button
          type="submit"
          disabled={loading || mostrarReautenticacao}
          className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? 'Salvando...' : 'Finalizar Cadastro'}
        </button>
      </form>
    </div>
  );
}
